import { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { FiMessageCircle, FiSend, FiPlus, FiTrash2, FiCheckCircle, FiXCircle } from 'react-icons/fi'

export default function CustomerContactChats({ website, customerAuthToken, customerAuthUser, selectedConversationId: selectedConversationIdProp, onSelectConversation }) {
  const [conversations, setConversations] = useState([])
  const [selectedConversationIdLocal, setSelectedConversationIdLocal] = useState(null)
  const [replyText, setReplyText] = useState('')
  const [selectedFiles, setSelectedFiles] = useState([])
  const [subject, setSubject] = useState('')
  const [newConversationOpen, setNewConversationOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState(null)

  // Use prop if provided (from notification center), otherwise use local state
  const selectedConversationId = selectedConversationIdProp || selectedConversationIdLocal
  
  const setSelectedConversationId = (id) => {
    setSelectedConversationIdLocal(id)
    if (onSelectConversation) {
      onSelectConversation(id)
    }
  }

  const selectedConversation = useMemo(() => {
    return conversations.find((conversation) => conversation._id === selectedConversationId) || null
  }, [conversations, selectedConversationId])

  const fetchConversations = async (nextPage = 1) => {
    if (!website?._id || !customerAuthToken) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const response = await axios.get('/contact-messages/conversations/customer/me', {
        params: { websiteId: website._id, page: nextPage, limit: 8 },
        headers: { Authorization: `Bearer ${customerAuthToken}` }
      })

      const nextConversations = response.data.data || []
      setConversations(nextConversations)
      setPagination(response.data.pagination || null)
      setPage(nextPage)
      if (!selectedConversationId && nextConversations.length > 0) {
        setSelectedConversationId(nextConversations[0]._id)
      }
    } catch (error) {
      console.error('[CustomerContactChats] Error loading conversations:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchConversations()
  }, [website?._id, customerAuthToken])

  useEffect(() => {
    if (selectedConversationIdProp && conversations.length > 0) {
      setSelectedConversationIdLocal(selectedConversationIdProp)
      // Scroll to the selected conversation if it's visible in the list
      const element = document.getElementById(`conversation-${selectedConversationIdProp}`)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }
  }, [selectedConversationIdProp, conversations])

  const handleSendReply = async (event) => {
    event.preventDefault()
    if (!selectedConversation || (!replyText.trim() && selectedFiles.length === 0)) return

    try {
      setSending(true)
      const formData = new FormData()
      formData.append('message', replyText.trim())
      selectedFiles.forEach((file) => formData.append('attachments', file))

      const response = await axios.post(
        `/contact-messages/conversations/${selectedConversation._id}/messages/customer`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${customerAuthToken}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      )

      const updatedConversation = response.data.data
      setConversations((prev) => prev.map((conversation) => conversation._id === updatedConversation._id ? updatedConversation : conversation))
      setReplyText('')
      setSelectedFiles([])
    } catch (error) {
      console.error('[CustomerContactChats] Error sending reply:', error)
    } finally {
      setSending(false)
    }
  }

  const handleStartConversation = async (event) => {
    event.preventDefault()
    if (!website?._id || (!replyText.trim() && selectedFiles.length === 0)) return

    try {
      setSending(true)
      const formData = new FormData()
      formData.append('websiteId', website._id)
      formData.append('subject', subject.trim() || 'New conversation')
      formData.append('message', replyText.trim())
      selectedFiles.forEach((file) => formData.append('attachments', file))

      const response = await axios.post('/contact-messages/conversations/customer/new', formData, {
        headers: {
          Authorization: `Bearer ${customerAuthToken}`,
          'Content-Type': 'multipart/form-data'
        }
      })

      const nextConversation = response.data.data
      setConversations((prev) => [nextConversation, ...prev])
      setSelectedConversationId(nextConversation._id)
      setReplyText('')
      setSubject('')
      setSelectedFiles([])
      setNewConversationOpen(false)
    } catch (error) {
      console.error('[CustomerContactChats] Error creating conversation:', error)
    } finally {
      setSending(false)
    }
  }

  const handleCloseConversation = async () => {
    if (!selectedConversation) return
    try {
      const response = await axios.post(`/contact-messages/conversations/${selectedConversation._id}/close`, {}, { headers: { Authorization: `Bearer ${customerAuthToken}` } })
      const updatedConversation = response.data.data
      setConversations((prev) => prev.map((conversation) => conversation._id === updatedConversation._id ? updatedConversation : conversation))
      toast.success('Conversation closed successfully')
    } catch (error) {
      console.error('[CustomerContactChats] Error closing conversation:', error)
      toast.error(error.response?.data?.message || 'Unable to close this conversation right now')
    }
  }

  const handleDeleteConversation = async () => {
    if (!selectedConversation || !window.confirm('Delete this conversation from your account?')) return
    try {
      await axios.delete(`/contact-messages/conversations/${selectedConversation._id}`, { headers: { Authorization: `Bearer ${customerAuthToken}` } })
      setConversations((prev) => prev.filter((conversation) => conversation._id !== selectedConversation._id))
      setSelectedConversationId(null)
    } catch (error) {
      console.error('[CustomerContactChats] Error deleting conversation:', error)
    }
  }

  if (!customerAuthUser || !customerAuthToken) {
    return null
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">Contact Us conversations</h3>
          <p className="text-sm text-gray-500">Follow up on your messages with the website owner.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setNewConversationOpen((prev) => !prev)}
            className="inline-flex items-center gap-2 rounded-lg border border-primary-200 px-3 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50"
          >
            <FiPlus /> New
          </button>
          <button
            type="button"
            onClick={() => fetchConversations(1)}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {newConversationOpen && (
        <form onSubmit={handleStartConversation} className="mb-4 rounded-lg border border-primary-200 bg-primary-50 p-4 space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <input
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Subject"
            />
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={(event) => setSelectedFiles(Array.from(event.target.files || []))}
              className="block w-full text-sm text-gray-500"
            />
          </div>
          <textarea
            value={replyText}
            onChange={(event) => setReplyText(event.target.value)}
            rows={4}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            placeholder="Start a new conversation with the website owner..."
          />
          {selectedFiles.length > 0 && <p className="text-xs text-gray-500">Selected attachments: {selectedFiles.map((file) => file.name).join(', ')}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setNewConversationOpen(false); setReplyText(''); setSubject(''); setSelectedFiles([]) }} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">Cancel</button>
            <button type="submit" disabled={sending || (!replyText.trim() && selectedFiles.length === 0)} className="rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white disabled:bg-gray-400">{sending ? 'Starting...' : 'Start conversation'}</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="py-6 text-center text-sm text-gray-500">Loading your conversations...</div>
      ) : conversations.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-600">
          No conversations yet. Send a message through the public Contact Us form and it will appear here.
        </div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-[240px_minmax(0,1fr)]">
          <div className="space-y-2">
            {conversations.map((conversation) => (
              <button
                key={conversation._id}
                id={`conversation-${conversation._id}`}
                type="button"
                onClick={() => setSelectedConversationId(conversation._id)}
                className={`w-full rounded-lg border p-3 text-left transition ${selectedConversationId === conversation._id ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
              >
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <FiMessageCircle className="text-primary-600" />
                  {conversation.subject || 'Contact request'}
                </div>
                <p className="mt-1 text-xs text-gray-500">{conversation.lastMessagePreview || conversation.message}</p>
              </button>
            ))}
          </div>

          {selectedConversation && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h4 className="text-base font-semibold text-gray-900">{selectedConversation.subject || 'Contact request'}</h4>
                  <p className="text-sm text-gray-500">{selectedConversation.customerName || selectedConversation.visitorName || customerAuthUser.email}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${selectedConversation.conversationStatus === 'closed' ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-700'}`}>
                    {selectedConversation.conversationStatus === 'closed' ? <FiXCircle /> : <FiCheckCircle />}
                    {selectedConversation.conversationStatus === 'closed' ? 'Closed' : 'Open'}
                  </span>
                  <button type="button" onClick={handleCloseConversation} className="rounded-lg border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50">Close</button>
                  <button type="button" onClick={handleDeleteConversation} className="rounded-lg border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50"><FiTrash2 className="inline" /></button>
                </div>
              </div>

              <div className="space-y-3">
                {(selectedConversation.messages || []).map((message) => (
                  <div key={message._id} className={`rounded-lg border p-3 ${message.sender === 'customer' ? 'border-gray-200 bg-white' : 'border-primary-200 bg-primary-50'}`}>
                    <div className="mb-1 flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <span>{message.senderName || (message.sender === 'customer' ? 'You' : 'Owner')}</span>
                      <span>{new Date(message.createdAt).toLocaleString()}</span>
                    </div>
                    <p className="whitespace-pre-wrap text-sm text-gray-700">{message.text}</p>
                    {(message.attachments || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(message.attachments || []).map((attachment, index) => (
                          <a key={`${message._id}-${index}`} href={attachment.url} target="_blank" rel="noreferrer" className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-primary-700 hover:bg-gray-50">
                            {attachment.filename || attachment.type || 'Attachment'}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <form onSubmit={handleSendReply} className="mt-4 space-y-3 border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700">Send a follow-up</label>
                {selectedConversation.conversationStatus === 'closed' && (
                  <div className="rounded-lg bg-gray-100 border border-gray-300 p-3 text-sm text-gray-700">
                    <p className="font-semibold mb-1">This conversation is closed</p>
                    <p>You cannot send messages on a closed conversation. The website owner can reopen it if they'd like to continue the conversation.</p>
                  </div>
                )}
                <textarea
                  value={replyText}
                  onChange={(event) => setReplyText(event.target.value)}
                  rows={4}
                  disabled={selectedConversation.conversationStatus === 'closed'}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
                  placeholder="Write a message to the website owner..."
                />
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={(event) => setSelectedFiles(Array.from(event.target.files || []))}
                  disabled={selectedConversation.conversationStatus === 'closed'}
                  className="block w-full text-sm text-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
                />
                {selectedFiles.length > 0 && (
                  <p className="text-xs text-gray-500">Selected attachments: {selectedFiles.map((file) => file.name).join(', ')}</p>
                )}
                <div className="flex justify-end">
                  <button type="submit" disabled={sending || (!replyText.trim() && selectedFiles.length === 0) || selectedConversation.conversationStatus === 'closed'} className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed">
                    <FiSend />
                    {sending ? 'Sending...' : 'Send reply'}
                  </button>
                </div>
                {pagination && (
                  <div className="flex items-center justify-between border-t border-gray-200 pt-3 text-sm text-gray-500">
                    <span>Page {pagination.page} / {pagination.pages}</span>
                    <div className="flex gap-2">
                      <button type="button" disabled={page <= 1} onClick={() => fetchConversations(page - 1)} className="rounded border px-2 py-1 disabled:opacity-50">Prev</button>
                      <button type="button" disabled={!pagination.hasMore} onClick={() => fetchConversations(page + 1)} className="rounded border px-2 py-1 disabled:opacity-50">Next</button>
                    </div>
                  </div>
                )}
              </form>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
